import { appConfig } from "../../../db.js";
import { collection, refs } from "../../Database/_variants/index.js";

const permissionFields = [
    ["stc", "DesignItems", "STC: Design Items"],
    ["stc", "EquipmentAssessments", "STC: Equipment Assessments"],
    ["stc", "DesignQueries", "STC: Design Queries"],
    ["military", "DesignItems", "Military: Design Items"],
    ["military", "EquipmentAssessments", "Military: Equipment Assessments"],
    ["military", "DesignQueries", "Military: Design Queries"],
].map(([variant, permission, label]) => ({
    name: `permissions[${variant}][${permission}]`,
    label,
    type: "toggle",
    required: false,
}));

export const addCustomer = ({ form }) => {
    form.setConfig({
        "title": "Add Customer",
        "type": "push",
        "location": "Database/Customers",
        "fields": [{
            "name": "Name",
            "label": "Name",
            "type": "text"
        }],
        preSubmitHook() {
            return { Date: { Created: new Date().toISOString() } };
        },
        postSubmitHook({ db, data, typeRef }) {
            return db.update(`Database/Customers/${typeRef.key}`, { ID: typeRef.key, Name: data.Name });
        }
    });
    form.toggle();
};

export const addMember = ({ form, pageItem, toast }) => {
    form.setConfig({
        "title": `Add Member to ${pageItem.Name}`,
        "type": "function",
        "functionName": "provisionCustomerMember",
        "fields": [{
            "name": "email",
            "label": "Email",
            "type": "text"
        }, {
            "name": "firstName",
            "label": "First Name",
            "type": "text"
        }, {
            "name": "lastName",
            "label": "Last Name",
            "type": "text"
        }, ...permissionFields],
        preSubmitHook() {
            return {
                customerId: pageItem.ID,
                databaseURL: appConfig.firebaseConfig.databaseURL
            };
        },
        postSubmitHook({ functionResult }) {
            toast.success(functionResult.createdAuthUser
                ? "Member created and invitation queued."
                : "Existing account linked to this customer.");
        }
    });
    form.toggle();
};

export const updateMember = ({ form, pageItem, rowItem, toast }) => {
    const permissions = Object.fromEntries(Object.entries(rowItem.Permissions || {}).flatMap(([variant, perms]) =>
        Object.entries(perms || {}).map(([perm, value]) => [`permissions[${variant}][${perm}]`, value === true])
    ));
    form.setValues({
        email: rowItem.Email,
        firstName: rowItem.FirstName,
        lastName: rowItem.LastName,
        ...permissions
    });
    form.setConfig({
        "title": `Update ${rowItem.FullName}`,
        "type": "function",
        "functionName": "updateCustomerMember",
        "fields": [{
            "name": "email",
            "label": "Email",
            "type": "text",
            "disabled": true
        }, {
            "name": "firstName",
            "label": "First Name",
            "type": "text"
        }, {
            "name": "lastName",
            "label": "Last Name",
            "type": "text"
        }, ...permissionFields],
        preSubmitHook() {
            return {
                uid: rowItem.ID,
                customerId: pageItem.ID,
                databaseURL: appConfig.firebaseConfig.databaseURL
            };
        },
        postSubmitHook() {
            toast.success("Member permissions updated.");
        }
    });
    form.toggle();
};

export const disableMember = ({ form, pageItem, rowItem, toast }) => {
    form.setConfig({
        "title": `Disable ${rowItem.FullName}`,
        "description": "This removes the member's customer access without disabling any other roles on their account.",
        "type": "function",
        "functionName": "disableCustomerMember",
        "fields": [],
        "submitButtonLabel": "Disable Member",
        "submitButtonClass": "cancel",
        preSubmitHook() {
            return {
                uid: rowItem.ID,
                customerId: pageItem.ID,
                databaseURL: appConfig.firebaseConfig.databaseURL
            };
        },
        postSubmitHook() {
            toast.success("Customer member disabled.");
        }
    });
    form.toggle();
};

export const makeGrantDesignChangeAccess = (variant) => ({ form, pageItem, store }) => {
    const designChangeCollection = collection(variant, "DesignChanges");
    const designChangeRefs = refs(variant, "DesignChanges");
    const label = variant.key === "military" ? "Military Design Change" : "Design Change";

    form.setConfig({
        "title": `Add ${label} Access`,
        "description": `Grant ${pageItem.Name} read-only access to an existing ${label}.`,
        "type": "ignoreAutoAction",
        "fields": [{
            "label": label,
            "name": "DesignChangeID",
            "type": "dropdown",
            "dataLocation": designChangeRefs,
            "labelKey": "Number",
            "idKey": "ID",
            "search": true,
            dataFilter(item) {
                return item.CustomerAccess?.Enabled !== true;
            }
        }],
        async preSubmitHook({ data, db }) {
            const customerAccess = {
                "Enabled": true,
                "CustomerID": pageItem.ID,
                "GrantedBy": store.getters.userName,
                "GrantedAt": new Date().toISOString(),
                "RevokedAt": null
            };

            await db.update(`${designChangeCollection}/${data.DesignChangeID}`, {
                "CustomerID": null,
                "CustomerAccess": customerAccess
            });
            await db.update(`${designChangeRefs}/${data.DesignChangeID}/CustomerAccess`, {
                "Enabled": true,
                "CustomerID": pageItem.ID
            });
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const makeRevokeDesignChangeAccess = (variant) => ({ form, rowItem, store }) => {
    const designChangeCollection = collection(variant, "DesignChanges");
    const designChangeRefs = refs(variant, "DesignChanges");

    form.setConfig({
        "title": `Revoke Customer Access to ${rowItem.Number}`,
        "description": "Customer members will immediately lose access to this Design Change and its projected items.",
        "submitButtonLabel": "Revoke Access",
        "submitButtonClass": "cancel",
        "type": "ignoreAutoAction",
        "fields": [],
        async preSubmitHook({ db }) {
            await db.update(`${designChangeCollection}/${rowItem.ID}/CustomerAccess`, {
                "Enabled": false,
                "RevokedBy": store.getters.userName,
                "RevokedAt": new Date().toISOString()
            });
            await db.set(`${designChangeRefs}/${rowItem.ID}/CustomerAccess/Enabled`, false);
        }
    });
    form.toggle();
};

